import { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Swal from 'sweetalert2';
import { FaArrowLeft, FaPlus, FaTrash, FaFilePdf, FaFileCode } from 'react-icons/fa';
import { getEstado, getEmisor, buscarReceptores, previsualizar, emitir, nuevoIdPeticion } from '../../services/facturadorService';
import {
  NOMBRES_TIPO, TIPOS_DIRECTOS, FORMAS_PAGO, CAMPOS_RECEPTOR, pesos, hoyChile, mensajeError, EstadoBadge, nombreDocumento,
} from './comun';
import '../../styles/pages/Facturador.css';

const RECEPTOR_VACIO = { rut: '', razonSocial: '', giro: '', direccion: '', comuna: '', ciudad: '', correoIntercambio: '' };
const LINEA_VACIA = { glosa: '', cantidad: '1', precioUnitario: '' };

/** Línea como la recibe la API. Nunca lleva "exenta": lo exento va en una factura exenta, no mezclado. */
const lineaParaEnviar = (linea, glosaPorDefecto = '') => ({
  glosa: linea.glosa.trim() || glosaPorDefecto,
  cantidad: Number(linea.cantidad || 1),
  precioUnitario: Number(linea.precioUnitario),
});

/** Solo los datos escritos: los que faltan los completa lo guardado del receptor. */
const receptorParaEnviar = (receptor) =>
  Object.fromEntries(
    Object.entries(receptor)
      .map(([campo, valor]) => [campo, String(valor ?? '').trim()])
      .filter(([, valor]) => valor !== '')
  );

const EmitirDocumentoPage = ({ id_company }) => {
  const navigate = useNavigate();
  const [estado, setEstado] = useState(null);
  const [formaPagoDefecto, setFormaPagoDefecto] = useState(null);
  const [error, setError] = useState(null);
  const [tipo, setTipo] = useState(null);
  const [receptor, setReceptor] = useState(RECEPTOR_VACIO);
  const [lineas, setLineas] = useState([{ ...LINEA_VACIA }]);
  const [montosConIva, setMontosConIva] = useState(false);
  const [formaPago, setFormaPago] = useState('');
  const [fechaEmision, setFechaEmision] = useState(hoyChile());
  const [sugerencias, setSugerencias] = useState([]);
  const [totales, setTotales] = useState(null);
  const [faltantes, setFaltantes] = useState([]);
  const [enviando, setEnviando] = useState(false);
  const [resultado, setResultado] = useState(null);
  const busqueda = useRef(0);

  useEffect(() => {
    Promise.all([getEstado(id_company), getEmisor(id_company).catch(() => null)])
      .then(([datos, emisor]) => {
        setEstado(datos);
        if (emisor) setFormaPagoDefecto(emisor.forma_pago_defecto);
        const habilitados = TIPOS_DIRECTOS.filter((t) => datos.tipos_habilitados?.includes(t));
        setTipo(habilitados[0] ?? null);
      })
      .catch((err) => setError(mensajeError(err, 'No se pudo cargar el facturador')));
  }, [id_company]);

  // Totales con el mismo cálculo del facturador, mientras se llena el formulario
  useEffect(() => {
    if (!tipo) return undefined;
    const validas = lineas.filter((l) => l.precioUnitario !== '' && Number(l.precioUnitario) >= 0 && Number(l.cantidad || 1) > 0);
    if (validas.length === 0) {
      setTotales(null);
      return undefined;
    }
    const temporizador = setTimeout(() => {
      previsualizar(id_company, {
        tipo,
        lineas: validas.map((l) => lineaParaEnviar(l, 'Ítem')),
        montosConIva: tipo !== 34 && montosConIva,
      })
        .then((r) => setTotales(r.totales))
        .catch(() => setTotales(null));
    }, 400);
    return () => clearTimeout(temporizador);
  }, [id_company, tipo, lineas, montosConIva]);

  const buscar = (texto) => {
    const id = ++busqueda.current;
    if (texto.trim().length < 3) {
      setSugerencias([]);
      return;
    }
    setTimeout(async () => {
      if (id !== busqueda.current) return;
      try {
        const lista = await buscarReceptores(id_company, texto);
        if (id === busqueda.current) setSugerencias(lista);
      } catch {
        setSugerencias([]);
      }
    }, 300);
  };

  const cambiarReceptor = (e) => {
    const { name, value } = e.target;
    setReceptor((r) => ({ ...r, [name]: value }));
    setFaltantes((f) => f.filter((campo) => campo !== name));
    if (name === 'rut') buscar(value);
  };

  const elegirReceptor = (guardado) => {
    setReceptor(Object.fromEntries(Object.keys(RECEPTOR_VACIO).map((campo) => [campo, guardado[campo] ?? ''])));
    setSugerencias([]);
    setFaltantes([]);
  };

  const cambiarLinea = (indice, campo, valor) =>
    setLineas((actuales) => actuales.map((l, i) => (i === indice ? { ...l, [campo]: valor } : l)));

  const nuevoDocumento = () => {
    setResultado(null);
    setReceptor(RECEPTOR_VACIO);
    setLineas([{ ...LINEA_VACIA }]);
    setMontosConIva(false);
    setFormaPago('');
    setFechaEmision(hoyChile());
    setTotales(null);
    setFaltantes([]);
  };

  const enviar = async (confirmarDuplicado = false) => {
    if (!receptor.rut.trim()) {
      Swal.fire({ icon: 'warning', title: 'Atención', text: 'Ingresa el RUT del receptor' });
      return;
    }
    if (lineas.some((l) => !l.glosa.trim() || l.precioUnitario === '')) {
      Swal.fire({ icon: 'warning', title: 'Atención', text: 'Cada línea necesita una glosa y un precio' });
      return;
    }
    setEnviando(true);
    setFaltantes([]);
    try {
      const solicitud = {
        tipo,
        receptor: receptorParaEnviar(receptor),
        lineas: lineas.map((l) => lineaParaEnviar(l)),
        montosConIva: tipo !== 34 && montosConIva,
        fechaEmision,
        ...(tipo !== 46 && formaPago ? { formaPago: Number(formaPago) } : {}),
        ...(confirmarDuplicado ? { confirmarDuplicado: true } : {}),
      };
      const documento = await emitir(id_company, solicitud, nuevoIdPeticion());
      setResultado(documento);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      const datos = err.response?.data;
      if (err.response?.status === 409 && datos?.duplicado) {
        const respuesta = await Swal.fire({
          icon: 'warning',
          title: '¿Emitir otra igual?',
          text: datos.message,
          showCancelButton: true,
          confirmButtonText: 'Sí, emitir otra',
          cancelButtonText: 'No',
        });
        if (respuesta.isConfirmed) await enviar(true);
        return;
      }
      if (datos?.faltantes) setFaltantes(datos.faltantes);
      Swal.fire({ icon: 'error', title: 'No se emitió', text: mensajeError(err, 'No se pudo emitir el documento') });
    } finally {
      setEnviando(false);
    }
  };

  const encabezado = (
    <header className="fe-header">
      <button className="fe-back-btn" onClick={() => navigate('/facturador')}>
        <FaArrowLeft /> Volver
      </button>
      <div className="fe-header-titulo">
        <div>
          <h1 className="fe-title">Emitir documento</h1>
          <p className="fe-sub">Se firma, se envía al SII y queda en tus documentos en el mismo paso</p>
        </div>
      </div>
    </header>
  );

  if (error) {
    return (
      <div className="fe-page">
        {encabezado}
        <div className="fe-aviso fe-aviso--rojo">{error}</div>
      </div>
    );
  }
  if (!estado) {
    return (
      <div className="fe-page">
        {encabezado}
        <p className="fe-loading">Cargando...</p>
      </div>
    );
  }
  if (!estado.activado || !tipo) {
    return (
      <div className="fe-page">
        {encabezado}
        <div className="fe-aviso fe-aviso--azul">
          {estado.activado ? 'Tu empresa no tiene habilitada la emisión de facturas.' : 'El facturador no está activado para esta empresa.'} Pídeselo a EFE.
        </div>
      </div>
    );
  }

  if (resultado) {
    const { uso } = resultado;
    return (
      <div className="fe-page">
        {encabezado}
        <div className="fe-resultado">
          <div className="fe-panel">
            <h2 className="fe-panel-title">{nombreDocumento(resultado)}</h2>
            <p><EstadoBadge estado={resultado.estado} /></p>
            {resultado.repetido && (
              <div className="fe-aviso fe-aviso--azul">Este pedido ya se había emitido: se muestra el documento original.</div>
            )}
            {resultado.estado === 'por_enviar' && (
              <div className="fe-aviso fe-aviso--amarillo">
                El SII todavía no recibe el documento; se reintenta solo. No lo entregues hasta que figure como enviado.
              </div>
            )}
            {resultado.estado === 'rechazado' && (
              <div className="fe-aviso fe-aviso--rojo">El SII rechazó el envío: {resultado.glosa_estado || 'sin detalle'}</div>
            )}
            <dl className="fe-datos">
              <dt>Receptor</dt>
              <dd>{resultado.razon_social_receptor} · {resultado.rut_receptor}</dd>
              {Number(resultado.monto_neto) > 0 && (<><dt>Neto</dt><dd>{pesos(resultado.monto_neto)}</dd></>)}
              {Number(resultado.monto_exento) > 0 && (<><dt>Exento</dt><dd>{pesos(resultado.monto_exento)}</dd></>)}
              {Number(resultado.iva) > 0 && (<><dt>IVA 19 %</dt><dd>{pesos(resultado.iva)}</dd></>)}
              <dt>Total</dt>
              <dd><strong>{pesos(resultado.monto_total)}</strong></dd>
            </dl>
            {uso && uso.cupo > 0 && (
              <p className="fe-hint">
                Con este documento llevas {uso.usados} de los {uso.cupo} incluidos este mes.
                {resultado.adicional ? ' Este documento es adicional.' : ''}
              </p>
            )}
            <div className="fe-form-actions">
              {resultado.pdf_url && (
                <a className="fe-btn fe-btn--primary" href={resultado.pdf_url} target="_blank" rel="noreferrer"><FaFilePdf /> PDF</a>
              )}
              {resultado.xml_url && (
                <a className="fe-btn fe-btn--secondary" href={resultado.xml_url} target="_blank" rel="noreferrer"><FaFileCode /> XML</a>
              )}
              <button className="fe-btn fe-btn--success" onClick={nuevoDocumento}>Emitir otro</button>
              <button className="fe-btn fe-btn--secondary" onClick={() => navigate('/facturador/documentos')}>Ver documentos</button>
            </div>
          </div>
          {resultado.pdf_url && <iframe className="fe-pdf" title="PDF del documento" src={resultado.pdf_url} />}
        </div>
      </div>
    );
  }

  const habilitados = TIPOS_DIRECTOS.filter((t) => estado.tipos_habilitados.includes(t));
  const esCompra = tipo === 46;
  const conIva = tipo !== 34;
  const marcar = (campo) => (faltantes.includes(campo) ? 'fe-input--falta' : '');

  return (
    <div className="fe-page">
      {encabezado}

      {!estado.listo && (
        <div className="fe-aviso fe-aviso--amarillo">
          Falta configurar el facturador antes de emitir. Revisa la <Link to="/facturador">portada del módulo</Link>.
        </div>
      )}

      <div className="fe-tipos">
        {habilitados.map((t) => (
          <button
            key={t}
            type="button"
            className={`fe-tipo-btn ${t === tipo ? 'fe-tipo-btn--activo' : ''}`}
            onClick={() => setTipo(t)}
          >
            {NOMBRES_TIPO[t]}
          </button>
        ))}
      </div>

      <div className="fe-panel fe-panel--wide">
        <h2 className="fe-panel-title">{esCompra ? 'Vendedor (a quien le compras)' : 'Receptor'}</h2>
        <div className="fe-form-grid">
          <div className="fe-form-group">
            <label>RUT *</label>
            <input
              name="rut"
              value={receptor.rut}
              onChange={cambiarReceptor}
              onBlur={() => setTimeout(() => setSugerencias([]), 200)}
              placeholder="12.345.678-9 o nombre"
              autoComplete="off"
              className={marcar('rut')}
            />
            {sugerencias.length > 0 && (
              <div className="fe-sugerencias">
                {sugerencias.map((s) => (
                  <button key={s.rut} type="button" className="fe-sugerencia" onMouseDown={() => elegirReceptor(s)}>
                    <strong>{s.razonSocial}</strong> <span className="fe-muted">{s.rut}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          {Object.entries(CAMPOS_RECEPTOR).map(([campo, etiqueta]) => (
            <div className="fe-form-group" key={campo}>
              <label>{etiqueta}</label>
              <input name={campo} value={receptor[campo]} onChange={cambiarReceptor} className={marcar(campo)} />
            </div>
          ))}
          <div className="fe-form-group">
            <label>Ciudad <span className="fe-muted">(opcional)</span></label>
            <input name="ciudad" value={receptor.ciudad} onChange={cambiarReceptor} />
          </div>
          <div className="fe-form-group">
            <label>Correo de intercambio <span className="fe-muted">(opcional)</span></label>
            <input name="correoIntercambio" type="email" value={receptor.correoIntercambio} onChange={cambiarReceptor} />
          </div>
        </div>
        <p className="fe-hint">
          La primera vez se piden la razón social, el giro, la dirección y la comuna; quedan guardados para las siguientes.
          Con el correo de intercambio, el XML le llega al receptor cuando el SII acepta el documento.
        </p>
      </div>

      <div className="fe-panel fe-panel--wide">
        <h2 className="fe-panel-title">Detalle</h2>
        {conIva && (
          <label className="fe-checkbox" style={{ marginBottom: 12 }}>
            <input type="checkbox" checked={montosConIva} onChange={(e) => setMontosConIva(e.target.checked)} />
            Los precios incluyen IVA
          </label>
        )}
        <table className="fe-lineas">
          <thead>
            <tr>
              <th>Glosa</th>
              <th className="fe-col-cant">Cantidad</th>
              <th className="fe-col-num">{conIva && montosConIva ? 'Precio con IVA' : conIva ? 'Precio neto' : 'Precio'}</th>
              <th className="fe-col-del" />
            </tr>
          </thead>
          <tbody>
            {lineas.map((linea, indice) => (
              <tr key={indice}>
                <td>
                  <input value={linea.glosa} onChange={(e) => cambiarLinea(indice, 'glosa', e.target.value)} placeholder="Qué vendes o compras, sin abreviaturas" />
                </td>
                <td className="fe-col-cant">
                  <input type="number" min="0" step="any" value={linea.cantidad} onChange={(e) => cambiarLinea(indice, 'cantidad', e.target.value)} />
                </td>
                <td className="fe-col-num">
                  <input type="number" min="0" step="any" value={linea.precioUnitario} onChange={(e) => cambiarLinea(indice, 'precioUnitario', e.target.value)} />
                </td>
                <td className="fe-col-del">
                  {lineas.length > 1 && (
                    <button type="button" className="fe-btn fe-btn--link" title="Quitar línea" onClick={() => setLineas((ls) => ls.filter((_, i) => i !== indice))}>
                      <FaTrash />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {lineas.length < 60 && (
          <button type="button" className="fe-btn fe-btn--secondary" onClick={() => setLineas((ls) => [...ls, { ...LINEA_VACIA }])}>
            <FaPlus /> Agregar línea
          </button>
        )}
        <p className="fe-hint">Si la glosa pasa de 80 caracteres, el resto va como descripción de la misma línea (Res. Ex. SII N° 36 de 2024).</p>

        {totales && (
          <div className="fe-totales">
            {totales.neto > 0 && <div><span>Neto</span><strong>{pesos(totales.neto)}</strong></div>}
            {totales.exento > 0 && <div><span>Exento</span><strong>{pesos(totales.exento)}</strong></div>}
            {totales.iva > 0 && <div><span>IVA 19 %</span><strong>{pesos(totales.iva)}</strong></div>}
            {totales.retenido > 0 && <div><span>IVA retenido</span><strong>−{pesos(totales.retenido)}</strong></div>}
            <div className="fe-total-final"><span>Total</span><strong>{pesos(totales.total)}</strong></div>
          </div>
        )}
      </div>

      <div className="fe-panel fe-panel--wide">
        <div className="fe-form-grid">
          {!esCompra && (
            <div className="fe-form-group">
              <label>Forma de pago</label>
              <select value={formaPago} onChange={(e) => setFormaPago(e.target.value)}>
                <option value="">
                  {formaPagoDefecto ? `La de siempre (${FORMAS_PAGO[formaPagoDefecto]})` : 'La de siempre'}
                </option>
                {Object.entries(FORMAS_PAGO).map(([valor, texto]) => (
                  <option key={valor} value={valor}>{texto}</option>
                ))}
              </select>
            </div>
          )}
          <div className="fe-form-group">
            <label>Fecha de emisión</label>
            <input type="date" value={fechaEmision} max={hoyChile()} onChange={(e) => setFechaEmision(e.target.value)} />
          </div>
        </div>
        <div className="fe-form-actions">
          <button type="button" className="fe-btn fe-btn--success" disabled={enviando} onClick={() => enviar()}>
            {enviando ? 'Emitiendo...' : `Emitir ${NOMBRES_TIPO[tipo].toLowerCase()}`}
          </button>
        </div>
        <p className="fe-hint">Revisa los datos antes de emitir: un documento emitido no se edita, se corrige con notas.</p>
      </div>
    </div>
  );
};

export default EmitirDocumentoPage;
